/**
 * RET-2191: the per-workspace notice that announces the Lead Agent cap raise
 * (RET-2140: Starter 130M, Growth 450M, Scale 1,200M, same price), as it
 * arrives from `GET {V2}workspace/{wid}/notices` (bff-web proxy over
 * workspace-service's `workspace_notices` table).
 *
 * The row is seeded by `workspace:seed-lead-agent-cap-notice` AFTER
 * leadbilling's backfill wrote the new cap, so "this workspace has the row"
 * already means "the raise reached it". The front never decides that itself:
 * no plan, entitlement or usage field can tell a raised workspace from one the
 * backfill has not reached yet.
 *
 * `wasCapped` is read by leadbilling BEFORE the backfill (after it, nobody
 * looks capped). It picks the "your agent can answer again" copy for the
 * workspaces the raise un-paused.
 */

/** The notice key the seeding command writes. Rows with any other key are not ours. */
export const LEAD_AGENT_CAP_RAISE_NOTICE_KEY = 'lead-agent-cap-raise-2026-10';

/** The alert family id in `ALERT_REGISTRY`. Same string on purpose: one notice, one family. */
export const LEAD_AGENT_CAP_RAISE_ALERT_ID = LEAD_AGENT_CAP_RAISE_NOTICE_KEY;

/** Mirror of leadbilling's `LeadAgentTier`. The copy has one body per tier. */
export const LEAD_AGENT_CAP_RAISE_TIERS = ['STARTER', 'GROWTH', 'SCALE'] as const;

export type tLeadAgentCapRaiseTier = (typeof LEAD_AGENT_CAP_RAISE_TIERS)[number];

export type tLeadAgentCapRaisePayload = {
  tier: tLeadAgentCapRaiseTier;
  wasCapped: boolean;
};

export type tLeadAgentCapRaiseNotice = {
  key: string;
  payload: tLeadAgentCapRaisePayload;
  visibleFrom: string;
  expiresAt: string;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

/**
 * Structural guard for the payload. A tier outside the list is a broken seed,
 * not a new tier: the copy would index a body key that does not exist. Like
 * the Meta notice, anything it cannot vouch for is `null`, and the caller
 * treats that exactly like "this workspace has no notice".
 */
export function parseLeadAgentCapRaisePayload(
  raw: unknown,
): tLeadAgentCapRaisePayload | null {
  const source = asRecord(raw);
  if (!source) return null;

  const tier =
    typeof source.tier === 'string' &&
    (LEAD_AGENT_CAP_RAISE_TIERS as readonly string[]).includes(source.tier)
      ? (source.tier as tLeadAgentCapRaiseTier)
      : null;
  if (!tier) return null;
  if (typeof source.wasCapped !== 'boolean') return null;

  return { tier, wasCapped: source.wasCapped };
}

/** One row of the endpoint's `data` array, validated end to end. */
export function parseLeadAgentCapRaiseNotice(
  raw: unknown,
): tLeadAgentCapRaiseNotice | null {
  const row = asRecord(raw);
  if (!row) return null;
  if (typeof row.key !== 'string' || row.key.trim().length === 0) return null;
  if (typeof row.visibleFrom !== 'string') return null;
  if (typeof row.expiresAt !== 'string') return null;

  const payload = parseLeadAgentCapRaisePayload(row.payload);
  if (!payload) return null;

  return {
    key: row.key,
    payload,
    visibleFrom: row.visibleFrom,
    expiresAt: row.expiresAt,
  };
}

/**
 * The endpoint's whole `data` array in, this workspace's live cap-raise
 * notice out. Same rules as `selectMetaPricingNotice`: the backend already
 * filters the window, the client re-checks the expiry for a tab parked across
 * it, and an unparseable date is NOT treated as expired (the fail-safe
 * direction is to keep a notice the backend chose to serve).
 */
export function selectLeadAgentCapRaiseNotice(
  data: unknown,
  now: number,
): tLeadAgentCapRaiseNotice | null {
  if (!Array.isArray(data)) return null;
  for (const raw of data) {
    const row = asRecord(raw);
    if (!row || row.key !== LEAD_AGENT_CAP_RAISE_NOTICE_KEY) continue;
    const notice = parseLeadAgentCapRaiseNotice(row);
    if (!notice) return null;
    const expiresAt = Date.parse(notice.expiresAt);
    if (!Number.isNaN(expiresAt) && expiresAt <= now) return null;
    return notice;
  }
  return null;
}

/** Whether two resolved notices say the same thing (no new reference on an unchanged refetch). */
export function isSameLeadAgentCapRaiseNotice(
  a: tLeadAgentCapRaiseNotice | null,
  b: tLeadAgentCapRaiseNotice | null,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.key === b.key &&
    a.visibleFrom === b.visibleFrom &&
    a.expiresAt === b.expiresAt &&
    a.payload.tier === b.payload.tier &&
    a.payload.wasCapped === b.payload.wasCapped
  );
}
